import React, { forwardRef } from "react";
import { Button as MuiButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { brand } from "../theme/theme";

// Si quien usa el botón fija su propio fondo vía sx, respetamos ese color
// en lugar de pintar el gradiente de marca por encima.
const hasCustomBackground = (sx) =>
  Boolean(
    sx &&
      (sx.backgroundColor ||
        sx.bgcolor ||
        sx.background ||
        sx["&:hover"]?.backgroundColor)
  );

export const Button = forwardRef(function Button(
  { variant = "default", size = "default", children, sx = {}, ...props },
  ref
) {
  const theme = useTheme();

  const getVariantStyles = (variant) => {
    const baseStyles = {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: "999px",
      fontSize: "0.875rem",
      boxShadow: "none",
      cursor: "pointer",
      "&:hover": {
        boxShadow: "none",
      },
      transition: "all 0.2s",
    };

    switch (variant) {
      case "default":
      case "primary":
        if (hasCustomBackground(sx)) {
          return {
            ...baseStyles,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              ...baseStyles["&:hover"],
              backgroundColor: theme.palette.primary.dark,
            },
          };
        }
        return {
          ...baseStyles,
          background: brand.gradient,
          color: theme.palette.primary.contrastText,
          boxShadow: brand.shadow,
          "&:hover": {
            background: brand.gradientHover,
            boxShadow: brand.shadowHover,
          },
          "&.Mui-disabled": {
            background: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
            boxShadow: "none",
          },
        };
      case "destructive":
        return {
          ...baseStyles,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: theme.palette.error.dark,
          },
        };
      case "outline":
        return {
          ...baseStyles,
          backgroundColor: "transparent",
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: theme.palette.action.hover,
          },
        };
      // Acción secundaria junto al botón de creación en las cabeceras de página.
      case "dark":
        return {
          ...baseStyles,
          backgroundColor: brand.inkFill,
          color: brand.inkFillText,
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: brand.inkHover,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
          },
        };
      case "white":
        return {
          ...baseStyles,
          backgroundColor: "#fff",
          color: "#0F172A",
          border: "1px solid #E2E8F0",
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: "#F8FAFC",
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
          },
        };
      case "secondary":
        return {
          ...baseStyles,
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: theme.palette.secondary.dark,
          },
        };
      case "ghost":
        return {
          ...baseStyles,
          backgroundColor: "transparent",
          color: theme.palette.text.primary,
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: theme.palette.action.hover,
          },
        };
      case "link":
        return {
          ...baseStyles,
          backgroundColor: "transparent",
          color: theme.palette.primary.main,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
          "&:hover": {
            ...baseStyles["&:hover"],
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        };
      default:
        return baseStyles;
    }
  };

  const getSizeStyles = (size) => {
    switch (size) {
      case "xs":
        return {
          height: "28px",
          padding: "4px 10px",
          fontSize: "0.8125rem",
          gap: "4px",
        };
      case "sm":
        return {
          height: "32px",
          padding: "6px 14px",
          fontSize: "0.875rem",
          gap: "6px",
        };
      case "lg":
        return {
          height: "40px",
          padding: "8px 26px",
          fontSize: "0.875rem",
          gap: "8px",
        };
      case "icon":
        return {
          width: "36px",
          height: "36px",
          minWidth: "36px",
          padding: "0",
        };
      default:
        return {
          height: "36px",
          padding: "8px 18px",
          fontSize: "0.875rem",
          gap: "8px",
        };
    }
  };

  return (
    <MuiButton
      ref={ref}
      sx={{
        ...getVariantStyles(variant),
        ...getSizeStyles(size),
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiButton>
  );
});
